import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card, CardBody, Row, Col,
  Table, Badge, Button, Input, InputGroup, Nav, NavItem, NavLink,
  Modal, ModalHeader, ModalBody, Spinner,
} from "reactstrap";
import { Search, Eye, XCircle, AlertTriangle, CheckCircle, Image, FileText, Clock, Upload, Printer } from "lucide-react";
import { PageFrame, PageBody } from "../../Components/Common/EmbeddablePage";
import {
  getAllFilledFormsPaged, getAllHazards, getHazardTree,
  updateFilledForm, createForm, saveAnswer,
  getAnswers, getPhotos, uploadPhoto, getPhotoUrl, getFormDetail,
  TYPE_NAMES, SCOPE_NAMES, TARGET_NAMES,
} from "../../services/sahaInspectionService";
import { getAuthHeader } from "../../Components/Contexts/BackendConstants";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

// 4.3 — Auth header gerektiren fotoğrafları blob URL'e dönüştür
async function fetchPhotoBlob(url) {
  if (!url) return null;
  try {
    const res = await axios.get(url, { headers: getAuthHeader(), responseType: "blob" });
    return URL.createObjectURL(res.data);
  } catch {
    return url; // hata olursa direkt URL dene
  }
}
async function resolvePhotos(list) {
  return Promise.all(
    (list || []).map(async (p) => {
      const raw = getPhotoUrl(p);
      const resolved = await fetchPhotoBlob(raw);
      return { ...p, _resolvedUrl: resolved };
    })
  );
}

const TABS_UYG = [
  { key: "open",   get label() { return i18n.t("saha:sahaUygunsuzlukList.acikUygunsuzluklar"); }, color: "warning"   },
  { key: "all",    get label() { return i18n.t("saha:sahaUygunsuzlukList.tumUygunsuzluklar"); },  color: "secondary" },
];
// DÖF (level=3) için tab yok — tüm DÖF formları zaten kapalıdır
const LEVEL_BADGE = {
  2: { get label() { return i18n.t("saha:sahaUygunsuzlukList.uygunsuzluk"); }, color: "warning" },
  3: { get label() { return i18n.t("saha:sahaUygunsuzlukList.kapaliDof"); },  color: "success" },
};
function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), { day: "2-digit", month: "short", year: "numeric" });
}
function statusBadge(f) {
  if (f.inspectionLevelId === 3 && f.isDone) return <Badge color="success" pill>{i18n.t("saha:sahaUygunsuzlukList.kapaliDof")}</Badge>;
  if (f.inspectionLevelId === 2 && !f.isDone) return <Badge color="danger" pill>{i18n.t("saha:sahaUygunsuzlukList.acik")}</Badge>;
  if (f.isDone) return <Badge color="primary" pill>{i18n.t("saha:sahaUygunsuzlukList.tamamlandi")}</Badge>;
  return <Badge color="secondary" pill>{i18n.t("saha:sahaUygunsuzlukList.devamEdiyor")}</Badge>;
}
const riskColor = (v) => v > 400 ? "danger" : v >= 71 ? "warning" : v >= 20 ? "info" : "success";
const riskLabel = (v) => v > 400 ? i18n.t("saha:sahaUygunsuzlukList.kabulEdilemez") : v >= 71 ? i18n.t("saha:sahaUygunsuzlukList.yuksekRisk") : v >= 20 ? i18n.t("saha:sahaUygunsuzlukList.ortaRisk") : i18n.t("saha:sahaUygunsuzlukList.dusukRisk");

// ─────────────────────────────────────────────────────────────────────────────
// embedded=true → sayfa kabuğu (page-content/Container/BreadCrumb) kapanır; sayfa
// Denetim Formu Doldur ekranının bir sekmesi olarak render edilir.
function SahaUygunsuzlukList({ levelFilter = 2, embedded = false }) {
  const { t: tx } = useTranslation("saha");
  const navigate = useNavigate();
  const TABS     = levelFilter === 3 ? null : TABS_UYG;
  const [activeTab, setActiveTab] = useState("open");
  const [search,    setSearch]    = useState("");
  const [allForms,  setAllForms]  = useState([]);
  const [closing,   setClosing]   = useState(null);

  // Kapat / DÖF Modal state
  const [closeModal, setCloseModal] = useState({ open: false, form: null, note: '', photoList: [], uploading: false, saving: false });
  const closePhotoRef = useRef(null);

  // Görüntüleme Modal state
  const [viewOpen,       setViewOpen]       = useState(false);
  const [viewForm,       setViewForm]       = useState(null);
  const [viewDetail,     setViewDetail]     = useState(null);
  const [viewAnswers,    setViewAnswers]    = useState({});
  const [viewPhotosNC,   setViewPhotosNC]   = useState([]); // inspectionLevelId=2 (Before)
  const [viewPhotosDOF,  setViewPhotosDOF]  = useState([]); // inspectionLevelId=3 (After)
  const [viewLoading,    setViewLoading]    = useState(false);

  // Bağımsız Modal state
  const [bagOpen,    setBagOpen]    = useState(false);
  const [bagStep,    setBagStep]    = useState("hazard");
  const [bagLoading, setBagLoading] = useState(false);
  const [bagHazards, setBagHazards] = useState([]);
  const [bagSelHaz,  setBagSelHaz]  = useState(null);
  const [bagTree,    setBagTree]    = useState(null);
  const [bagSelRisk, setBagSelRisk] = useState(null);
  const [bagSelNc,   setBagSelNc]   = useState(null);
  const [bagSaving,  setBagSaving]  = useState(false);

  // The selected fair and every page of it (#601): the bare call listed the first 50 forms of all fairs.
  const loadForms = useCallback(() => {
    const fuarId = Number(localStorage.getItem('activeFairId')) || undefined;
    getAllFilledFormsPaged({ fuarId, inspectionLevelId: levelFilter }).then((data) =>
      setAllForms(data.filter((f) => f.inspectionLevelId === levelFilter))
    );
  }, [levelFilter]);

  useEffect(() => {
    document.title = levelFilter === 3 ? i18n.t("saha:sahaUygunsuzlukList.dofListesiFdsSaha") : i18n.t("saha:sahaUygunsuzlukList.uygunsuzlukListesiFdsSaha");
    loadForms();
  }, [loadForms, levelFilter]);

  const filtered = allForms.filter((f) => {
    // levelFilter=3 (DÖF): tab yok, tüm kayıtlar gösterilir
    const matchTab =
      levelFilter === 3 ||
      activeTab === "all"    ||
      (activeTab === "open"   && !f.isDone) ||
      (activeTab === "closed" &&  f.isDone);
    const matchSearch = [
      TARGET_NAMES[f.scopeTargetId] || "",
      TYPE_NAMES[f.inspectionTypeId] || "",
      SCOPE_NAMES[f.inspectionScopeId] || "",
      String(f.id),
    ].join(" ").toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const openCount   = allForms.filter((f) => !f.isDone).length;
  const closedCount = allForms.filter((f) =>  f.isDone).length;

  // ── Görüntüleme ───────────────────────────────────────────────────────────
  async function handleView(form) {
    setViewForm(form);
    setViewDetail(null);
    setViewAnswers({});
    setViewPhotosNC([]);
    setViewPhotosDOF([]);
    setViewOpen(true);
    setViewLoading(true);
    try {
      const [detail, answers, rawNC, rawDOF] = await Promise.all([
        getFormDetail(form.id),
        getAnswers(form.id),
        getPhotos(form.id, { inspectionLevelId: 2 }),
        getPhotos(form.id, { inspectionLevelId: 3 }),
      ]);
      setViewDetail(detail);
      setViewAnswers(answers);
      // 4.3: auth-gated fotoğrafları blob URL'e çevir
      const [pNC, pDOF] = await Promise.all([
        resolvePhotos(rawNC),
        resolvePhotos(rawDOF),
      ]);
      setViewPhotosNC(pNC);
      setViewPhotosDOF(pDOF);
    } catch (err) {
      console.error('handleView:', err);
    } finally {
      setViewLoading(false);
    }
  }

  // ── Kapat / DÖF Modal ─────────────────────────────────────────────────────
  function handleOpenCloseModal(form) {
    setCloseModal({ open: true, form, note: '', photoList: [], uploading: false, saving: false });
  }

  async function handleClosePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file || !closeModal.form) return;
    e.target.value = '';
    const fuarId = Number(localStorage.getItem('activeFairId') || 3);
    setCloseModal((prev) => ({ ...prev, uploading: true }));
    try {
      const doc = await uploadPhoto(closeModal.form.id, file, {
        fuarId,
        questionId:              null,
        inspectionLevelId:       3,
        inspectionFormContentId: null,
      });
      setCloseModal((prev) => ({ ...prev, uploading: false, photoList: [...prev.photoList, doc] }));
    } catch (err) {
      console.error('DÖF foto hatası:', err);
      setCloseModal((prev) => ({ ...prev, uploading: false }));
    }
  }

  async function handleConfirmClose() {
    if (!closeModal.form) return;
    setCloseModal((prev) => ({ ...prev, saving: true }));
    try {
      await updateFilledForm(closeModal.form.id, {
        inspectionLevelId: 3,
        isDone:            true,
        completedAt:       new Date().toISOString(),
        ...(closeModal.note ? { notes: closeModal.note } : {}),
      });
      setCloseModal({ open: false, form: null, note: '', photoList: [], uploading: false, saving: false });
      loadForms();
    } catch (err) {
      console.error('Kapat hatası:', err);
      setCloseModal((prev) => ({ ...prev, saving: false }));
    }
  }

  // ── Bağımsız Uygunsuzluk Modal ────────────────────────────────────────────
  function openBagModal() {
    setBagOpen(true);
    setBagStep("hazard");
    setBagSelHaz(null); setBagTree(null); setBagSelRisk(null); setBagSelNc(null);
    setBagLoading(true);
    getAllHazards({ noParentOnly: true }).then((d) => { setBagHazards(d); setBagLoading(false); });
  }

  async function handleBagHazardSelect(hazard) {
    setBagSelHaz(hazard); setBagSelRisk(null); setBagSelNc(null);
    setBagLoading(true);
    const tree = await getHazardTree(hazard.inspection_hazard_id);
    setBagTree(tree); setBagStep("risk"); setBagLoading(false);
  }

  function handleBagRiskSelect(risk) {
    setBagSelRisk(risk); setBagSelNc(null); setBagStep("nc");
  }

  async function handleBagSave() {
    if (!bagSelNc || !bagSelRisk || !bagSelHaz) return;
    const fuarId = Number(localStorage.getItem('activeFairId') || 3);
    const userId = Number(localStorage.getItem('userId') || 17);
    setBagSaving(true);
    try {
      // A standalone nonconformity is always its own form (POST /form): checkOrSave with null keys used to hand
      // every one of a fair the same document. It has no question, so its content row carries none.
      const form = await createForm({
        inspectionLevelId: 2, inspectionTypeId: null,
        inspectionScopeId: null, scopeTargetId: null, fuarId, userId,
      });
      await saveAnswer(form.id, null, {
        value: 2,
        hazardId: bagSelHaz.inspection_hazard_id,
        riskId: bagSelRisk.riskId,
        nonconformityId: bagSelNc.nonconformityId,
      }, 2);
      setBagOpen(false);
      loadForms();
    } catch (err) {
      console.error('Bağımsız uygunsuzluk kayıt hatası:', err);
      alert(tx("sahaUygunsuzlukList.kayitSirasindaBirHataOlustu"));
    } finally {
      setBagSaving(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageFrame embedded={embedded}>
      <PageBody
        embedded={embedded}
        title={levelFilter === 3 ? tx("sahaUygunsuzlukList.dofListesi") : tx("sahaUygunsuzlukList.uygunsuzlukListesi")}
        pageTitle={tx("sahaUygunsuzlukList.fdsSaha")}
      >

        <Row className="g-3 mb-4">
          {levelFilter === 3 ? (
            <Col xs={6} md={3}>
              <Card className="border-0 shadow-sm">
                <CardBody className="d-flex align-items-center gap-3">
                  <CheckCircle size={28} className="text-success flex-shrink-0" />
                  <div>
                    <div className="fw-bold fs-4">{allForms.length}</div>
                    <div className="text-muted small">{tx("sahaUygunsuzlukList.toplamDof")}</div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ) : (
            <>
              <Col xs={6} md={3}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="d-flex align-items-center gap-3">
                    <AlertTriangle size={28} className="text-danger flex-shrink-0" />
                    <div>
                      <div className="fw-bold fs-4">{openCount}</div>
                      <div className="text-muted small">{tx("sahaUygunsuzlukList.acikUygunsuzluk")}</div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="d-flex align-items-center gap-3">
                    <CheckCircle size={28} className="text-success flex-shrink-0" />
                    <div>
                      <div className="fw-bold fs-4">{closedCount}</div>
                      <div className="text-muted small">{tx("sahaUygunsuzlukList.kapaliUygunsuzluk")}</div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </>
          )}
        </Row>

        <Card className="border-0 shadow-sm">
          <CardBody>
            {/* Başlık + Butonlar */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <h6 className="fw-semibold mb-0">
                {levelFilter === 3 ? tx("sahaUygunsuzlukList.dofListesi") : tx("sahaUygunsuzlukList.uygunsuzluklar")}
              </h6>
              {levelFilter !== 3 && (
                <div className="d-flex gap-2 flex-wrap">
                  <Button color="danger" size="sm" className="d-flex align-items-center gap-1" onClick={openBagModal}>
                    <AlertTriangle size={14} />{" "}{tx("sahaUygunsuzlukList.bagimsizUygunsuzlukOlustur")}
                  </Button>
                  <Button color="success" size="sm" className="d-flex align-items-center gap-1"
                    onClick={() => navigate("/FdsSaha/Formlar/uygunsuzluk-olustur")}>
                    <CheckCircle size={14} />{" "}{tx("sahaUygunsuzlukList.hiyerarsiUygunsuzlukOlustur")}
                  </Button>
                </div>
              )}
            </div>

            {TABS && (
              <Nav tabs className="mb-3">
                {TABS.map((t) => (
                  <NavItem key={t.key}>
                    <NavLink className={activeTab === t.key ? "active fw-semibold" : "text-muted"}
                      style={{ cursor: "pointer" }} onClick={() => setActiveTab(t.key)}>
                      {t.label}
                      <Badge color={t.color} pill className="ms-2">
                        {t.key === "all"    ? allForms.length :
                         t.key === "open"   ? openCount :
                         t.key === "closed" ? closedCount : 0}
                      </Badge>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            )}

            <InputGroup className="mb-3" style={{ maxWidth: 320 }}>
              <span className="input-group-text bg-white border-end-0"><Search size={15} className="text-muted" /></span>
              <Input className="border-start-0" placeholder={tx("sahaUygunsuzlukList.ara")} value={search} onChange={(e) => setSearch(e.target.value)} />
            </InputGroup>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0 small">
                <thead className="table-light">
                  <tr>
                    <th>{tx("sahaUygunsuzlukList.tur")}</th><th>{tx("sahaUygunsuzlukList.denetimTipi")}</th><th>{tx("sahaUygunsuzlukList.hedef")}</th>
                    <th>{tx("sahaUygunsuzlukList.durum")}</th><th>{tx("sahaUygunsuzlukList.olusturulma")}</th><th>{tx("sahaUygunsuzlukList.tamamlanma")}</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-muted py-4">{tx("common:kayitYok")}</td></tr>
                  )}
                  {filtered.map((f) => {
                    const lvl      = LEVEL_BADGE[f.inspectionLevelId] || { label: "—", color: "secondary" };
                    const canClose = f.inspectionLevelId === 2 && !f.isDone;
                    return (
                      <tr key={f.id}>
                        <td><Badge color={lvl.color} pill>{lvl.label}</Badge></td>
                        <td className="text-muted">{TYPE_NAMES[f.inspectionTypeId] || "—"}</td>
                        <td>
                          <div className="fw-semibold">{TARGET_NAMES[f.scopeTargetId] || f.scopeTargetId || "—"}</div>
                          <div className="text-muted" style={{ fontSize: "0.7rem" }}>{SCOPE_NAMES[f.inspectionScopeId] || "—"}</div>
                        </td>
                        <td>{statusBadge(f)}</td>
                        <td className="text-muted">{formatDate(f.createdAt)}</td>
                        <td className="text-muted">{formatDate(f.completedAt)}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button size="sm" color="light" className="border" title={tx("common:goruntule")} onClick={() => handleView(f)}><Eye size={13} /></Button>
                            <Button size="sm" color="light" className="border" title={tx("sahaUygunsuzlukList.yazdirDofRaporu")}
                              onClick={() => window.open(`/FdsSaha/dof-print/${f.id}`, "_blank")}>
                              <Printer size={13} />
                            </Button>
                            {canClose && (
                              <Button size="sm" color="success" outline title={tx("sahaUygunsuzlukList.kapatDofOlustur")}
                                onClick={() => handleOpenCloseModal(f)}>
                                <CheckCircle size={13} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </PageBody>

      {/* ── Kapat / DÖF Onay Modalı ─────────────────────────────────────── */}
      <Modal isOpen={closeModal.open} toggle={() => setCloseModal((p) => ({ ...p, open: false }))} centered>
        <ModalHeader toggle={() => setCloseModal((p) => ({ ...p, open: false }))}>
          <CheckCircle size={16} className="text-success me-2" />
          {tx("sahaUygunsuzlukList.uygunsuzluguKapatDofOlustur")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-3">
            {tx("sahaUygunsuzlukList.buUygunsuzluguKapatmakVeDof")}
          </p>
          {/* Kapanış notu */}
          <div className="mb-3">
            <label className="form-label small fw-semibold">{tx("sahaUygunsuzlukList.kapanisNotuIstegeBagli")}</label>
            <textarea
              className="form-control form-control-sm"
              rows={3}
              placeholder={tx("sahaUygunsuzlukList.yapilanDuzeltmeAlinanOnlem")}
              value={closeModal.note}
              onChange={(e) => setCloseModal((p) => ({ ...p, note: e.target.value }))}
            />
          </div>
          {/* DÖF Kapanış Fotoğrafı — Level 3 */}
          <div className="p-3 bg-light rounded-3 border mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="form-label fw-semibold small text-uppercase text-muted mb-0">
                <Image size={13} className="me-1" />{" "}{tx("sahaUygunsuzlukList.kapanisFotografiIstegeBagli")}
              </label>
              <button
                className="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
                onClick={() => closePhotoRef.current?.click()}
                disabled={closeModal.uploading}
              >
                {closeModal.uploading ? <Spinner size="sm" /> : <><Upload size={13} />{" "}{tx("sahaUygunsuzlukList.fotografCek")}</>}
              </button>
            </div>
            <input
              ref={closePhotoRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleClosePhotoChange}
            />
            {closeModal.photoList.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mt-2">
                {closeModal.photoList.map((p) => (
                  <span key={p.id} className="badge bg-success rounded-pill small">
                    <CheckCircle size={10} className="me-1" />{p.originalFileName}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button color="light" onClick={() => setCloseModal((p) => ({ ...p, open: false }))} disabled={closeModal.saving}>
              {tx("common:iptal")}
            </Button>
            <Button color="success" onClick={handleConfirmClose} disabled={closeModal.saving || closeModal.uploading}>
              {closeModal.saving
                ? <><Spinner size="sm" className="me-1" />{tx("sahaUygunsuzlukList.kaydediliyor")}</>
                : <><CheckCircle size={14} className="me-1" />{tx("sahaUygunsuzlukList.onaylaKapat")}</>}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      {/* ── Görüntüleme Modal ────────────────────────────────────────────── */}
      <Modal isOpen={viewOpen} toggle={() => setViewOpen(false)} size="lg" centered scrollable>
        <ModalHeader toggle={() => setViewOpen(false)}>
          <Eye size={16} className="me-2 text-primary" />
          {tx("sahaUygunsuzlukList.formDetayi")}
          {viewForm && <small className="text-muted ms-2 fw-normal" style={{ fontSize: "0.7rem" }}>#{viewForm.id}</small>}
        </ModalHeader>
        <ModalBody>
          {viewLoading && <div className="text-center py-5"><Spinner color="primary" /></div>}
          {!viewLoading && viewForm && (
            <>
              {/* Meta bilgiler */}
              <div className="p-3 rounded-3 bg-light border mb-3">
                <Row className="g-2 small">
                  <Col xs={6} md={4}>
                    <div className="text-muted">{tx("sahaUygunsuzlukList.seviye")}</div>
                    <div className="fw-semibold">{statusBadge(viewForm)}</div>
                  </Col>
                  <Col xs={6} md={4}>
                    <div className="text-muted">{tx("sahaUygunsuzlukList.denetimTipi")}</div>
                    <div className="fw-semibold">
                      {viewDetail?.inspectionTypeName || TYPE_NAMES[viewForm.inspectionTypeId] || `#${viewForm.inspectionTypeId ?? "—"}`}
                    </div>
                  </Col>
                  <Col xs={6} md={4}>
                    <div className="text-muted">{tx("sahaUygunsuzlukList.kapsam")}</div>
                    <div className="fw-semibold">
                      {viewDetail?.inspectionScopeName || SCOPE_NAMES[viewForm.inspectionScopeId] || `#${viewForm.inspectionScopeId ?? "—"}`}
                    </div>
                  </Col>
                  <Col xs={6} md={4}>
                    <div className="text-muted">{tx("sahaUygunsuzlukList.hedef")}</div>
                    <div className="fw-semibold">
                      {viewDetail?.scopeTargetName || TARGET_NAMES[viewForm.scopeTargetId] || viewForm.scopeTargetId || "—"}
                    </div>
                  </Col>
                  {(viewDetail?.firmaName) && (
                    <Col xs={6} md={4}>
                      <div className="text-muted">{tx("sahaUygunsuzlukList.firma")}</div>
                      <div className="fw-semibold">{viewDetail.firmaName}</div>
                    </Col>
                  )}
                  {(viewDetail?.userName) && (
                    <Col xs={6} md={4}>
                      <div className="text-muted">{tx("sahaUygunsuzlukList.denetci")}</div>
                      <div className="fw-semibold">{viewDetail.userName} {viewDetail.userSurname || ""}</div>
                    </Col>
                  )}
                  <Col xs={6} md={4}>
                    <div className="text-muted d-flex align-items-center gap-1"><Clock size={12} />{" "}{tx("sahaUygunsuzlukList.olusturulma")}</div>
                    <div className="fw-semibold">{formatDate(viewForm.createdAt)}</div>
                  </Col>
                  <Col xs={6} md={4}>
                    <div className="text-muted d-flex align-items-center gap-1"><Clock size={12} />{" "}{tx("sahaUygunsuzlukList.tamamlanma")}</div>
                    <div className="fw-semibold">{formatDate(viewForm.completedAt)}</div>
                  </Col>
                  {viewForm.notes && (
                    <Col xs={12}>
                      <div className="text-muted d-flex align-items-center gap-1"><FileText size={12} />{" "}{tx("sahaUygunsuzlukList.notlar")}</div>
                      <div className="fw-semibold">{viewForm.notes}</div>
                    </Col>
                  )}
                </Row>
              </div>

              {/* Cevaplar */}
              {viewDetail?.contents?.length > 0 ? (
                <>
                  <h6 className="fw-semibold mb-2 small text-uppercase text-muted">{tx("sahaUygunsuzlukList.cevaplar", { length: viewDetail.contents.length })}</h6>
                  <div className="d-flex flex-column gap-2 mb-3">
                    {viewDetail.contents.map((c) => {
                      const isUygunsuz = c.answerValue === 2;
                      return (
                        <div key={c.questionId} className={`p-2 rounded-3 border d-flex align-items-start gap-2 ${isUygunsuz ? "border-danger bg-danger bg-opacity-10" : "border-success bg-success bg-opacity-10"}`}>
                          <span className={`mt-1 flex-shrink-0 ${isUygunsuz ? "text-danger" : "text-success"}`}>
                            {isUygunsuz ? <XCircle size={14} /> : <CheckCircle size={14} />}
                          </span>
                          <div className="small">
                            {c.categoryName && <div className="text-muted" style={{ fontSize: "0.7rem" }}>{c.categoryName}</div>}
                            <div className="fw-semibold">{c.questionName || tx("sahaUygunsuzlukList.soru", { questionId: c.questionId })}</div>
                            <div className="text-muted">{isUygunsuz ? tx("sahaUygunsuzlukList.uygunsuz") : c.answerValue === 1 ? tx("sahaUygunsuzlukList.uygun") : tx("sahaUygunsuzlukList.deger", { answerValue: c.answerValue })}</div>
                            {c.nonconformityName && <div className="text-danger" style={{ fontSize: "0.7rem" }}>⚠ {c.nonconformityName}</div>}
                            {c.hazardName && <div className="text-warning" style={{ fontSize: "0.7rem" }}>🔶 {c.hazardName}</div>}
                            {c.riskName && <div className="text-secondary" style={{ fontSize: "0.7rem" }}>📋 {c.riskName}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : Object.keys(viewAnswers).length === 0 ? (
                <p className="text-center text-muted small py-3">{tx("sahaUygunsuzlukList.buFormIcinKayitliCevap")}</p>
              ) : (
                <>
                  <h6 className="fw-semibold mb-2 small text-uppercase text-muted">{tx("sahaUygunsuzlukList.cevaplar", { length: Object.keys(viewAnswers).length })}</h6>
                  <div className="d-flex flex-column gap-2 mb-3">
                    {Object.entries(viewAnswers).map(([qId, ans]) => {
                      const isUygunsuz = ans.value === 2;
                      return (
                        <div key={qId} className={`p-2 rounded-3 border d-flex align-items-start gap-2 ${isUygunsuz ? "border-danger bg-danger bg-opacity-10" : "border-success bg-success bg-opacity-10"}`}>
                          <span className={`mt-1 flex-shrink-0 ${isUygunsuz ? "text-danger" : "text-success"}`}>
                            {isUygunsuz ? <XCircle size={14} /> : <CheckCircle size={14} />}
                          </span>
                          <div className="small">
                            <div className="fw-semibold">{tx("sahaUygunsuzlukList.soru2")}{qId}</div>
                            <div className="text-muted">{isUygunsuz ? tx("sahaUygunsuzlukList.uygunsuz") : ans.value === 1 ? tx("sahaUygunsuzlukList.uygun") : tx("sahaUygunsuzlukList.deger2", { value: ans.value })}</div>
                            {ans.nonconformityId && <div className="text-danger" style={{ fontSize: "0.7rem" }}>{tx("sahaUygunsuzlukList.uygunsuzlukId")}{" "}{ans.nonconformityId}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Fotoğraflar — Before (Level 2) / After (Level 3) */}
              {(viewPhotosNC.length > 0 || viewPhotosDOF.length > 0) && (
                <>
                  <h6 className="fw-semibold mb-2 small text-uppercase text-muted d-flex align-items-center gap-1">
                    <Image size={13} />{" "}{tx("sahaUygunsuzlukList.fotograflar")}
                  </h6>
                  {viewPhotosNC.length > 0 && (
                    <div className="mb-3">
                      <div className="small fw-semibold text-warning mb-2 d-flex align-items-center gap-1">
                        <AlertTriangle size={12} />{" "}{tx("sahaUygunsuzlukList.uygunsuzlukFotograflariBefore", { length: viewPhotosNC.length })}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {viewPhotosNC.map((p) => {
                          const url = p._resolvedUrl || getPhotoUrl(p);
                          return url ? (
                            <a key={p.id} href={url} target="_blank" rel="noreferrer" title={p.originalFileName}>
                              <img
                                src={url}
                                alt={p.originalFileName}
                                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '2px solid #ffc107' }}
                              />
                            </a>
                          ) : (
                            <div key={p.id} className="border border-warning rounded-2 p-2 bg-warning bg-opacity-10" style={{ fontSize: '0.7rem', maxWidth: 150 }}>
                              <div className="fw-semibold text-truncate">{p.originalFileName}</div>
                              <div className="text-muted">{formatDate(p.createdAt)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {viewPhotosDOF.length > 0 && (
                    <div className="mb-3">
                      <div className="small fw-semibold text-success mb-2 d-flex align-items-center gap-1">
                        <CheckCircle size={12} />{" "}{tx("sahaUygunsuzlukList.kapanisFotograflariAfter", { length: viewPhotosDOF.length })}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {viewPhotosDOF.map((p) => {
                          const url = p._resolvedUrl || getPhotoUrl(p);
                          return url ? (
                            <a key={p.id} href={url} target="_blank" rel="noreferrer" title={p.originalFileName}>
                              <img
                                src={url}
                                alt={p.originalFileName}
                                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '2px solid #198754' }}
                              />
                            </a>
                          ) : (
                            <div key={p.id} className="border border-success rounded-2 p-2 bg-success bg-opacity-10" style={{ fontSize: '0.7rem', maxWidth: 150 }}>
                              <div className="fw-semibold text-truncate">{p.originalFileName}</div>
                              <div className="text-muted">{formatDate(p.createdAt)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>

      {/* ── Bağımsız Uygunsuzluk Modal ──────────────────────────────────── */}
      <Modal isOpen={bagOpen} toggle={() => setBagOpen(false)} size="lg" centered scrollable>
        <ModalHeader toggle={() => setBagOpen(false)}>
          <AlertTriangle size={16} className="text-danger me-2" />
          {tx("sahaUygunsuzlukList.bagimsizUygunsuzlukOlustur2")}
        </ModalHeader>
        <ModalBody>
          {/* Adım göstergesi */}
          <div className="d-flex gap-2 mb-4">
            {[tx("sahaUygunsuzlukList.uygunsuzlukSec"), tx("sahaUygunsuzlukList.tehlikeSec"), tx("sahaUygunsuzlukList.riskSec")].map((lbl, i) => {
              const keys    = ["hazard", "risk", "nc"];
              const current = keys.indexOf(bagStep);
              return (
                <span key={lbl} className={`badge rounded-pill px-3 py-2 ${
                  i === current ? "bg-danger text-white" : i < current ? "bg-success text-white" : "bg-light text-muted border"
                }`}>{i + 1}. {lbl}</span>
              );
            })}
          </div>

          {bagLoading && <div className="text-center py-4"><Spinner color="danger" /></div>}

          {/* Adım 1 — Hazard */}
          {!bagLoading && bagStep === "hazard" && (
            <div>
              <p className="text-muted small mb-3">{tx("sahaUygunsuzlukList.bagimsizUygunsuzluklardanBiriniSecin")}</p>
              {bagHazards.length === 0 && <p className="text-center text-muted py-3">{tx("sahaUygunsuzlukList.bagimsizUygunsuzlukBulunamadi")}</p>}
              <div className="d-flex flex-column gap-2">
                {bagHazards.map((h) => (
                  <button key={h.inspection_hazard_id}
                    className="btn btn-light border rounded-3 text-start p-3 d-flex align-items-center gap-2"
                    onClick={() => handleBagHazardSelect(h)}>
                    <AlertTriangle size={15} className="text-warning flex-shrink-0" />
                    <span className="small fw-semibold">{h.translated_name || h.inspection_hazard_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Adım 2 — Risk */}
          {!bagLoading && bagStep === "risk" && bagTree && (
            <div>
              <div className="mb-3 p-2 bg-warning bg-opacity-10 rounded-3 border border-warning border-opacity-25">
                <span className="small fw-semibold text-warning">
                  <AlertTriangle size={13} className="me-1" />{bagTree.hazardName}
                </span>
              </div>
              <p className="text-muted small mb-3">{tx("sahaUygunsuzlukList.tehlikeRiskSecin")}</p>
              <div className="d-flex flex-column gap-2">
                {(bagTree.risks || []).map((r) => (
                  <button key={r.riskId}
                    className="btn btn-light border rounded-3 text-start p-3 d-flex align-items-center gap-2"
                    onClick={() => handleBagRiskSelect(r)}>
                    <XCircle size={15} className="text-danger flex-shrink-0" />
                    <span className="small fw-semibold">{r.riskName}</span>
                  </button>
                ))}
              </div>
              <button className="btn btn-link btn-sm text-muted p-0 mt-3" onClick={() => setBagStep("hazard")}>{tx("sahaUygunsuzlukList.geri")}</button>
            </div>
          )}

          {/* Adım 3 — Nonconformity */}
          {!bagLoading && bagStep === "nc" && bagSelRisk && (
            <div>
              <div className="mb-3 p-2 bg-danger bg-opacity-10 rounded-3 border border-danger border-opacity-25">
                <span className="small fw-semibold text-danger">
                  <XCircle size={13} className="me-1" />{bagSelRisk.riskName}
                </span>
              </div>
              <p className="text-muted small mb-3">{tx("sahaUygunsuzlukList.riskSecin")}</p>
              <div className="d-flex flex-column gap-2">
                {(bagSelRisk.nonconformities || []).map((nc) => (
                  <button key={nc.nonconformityId}
                    className={`btn border rounded-3 text-start p-3 ${bagSelNc?.nonconformityId === nc.nonconformityId ? "btn-primary border-primary" : "btn-light"}`}
                    onClick={() => setBagSelNc(nc)}>
                    <div className="d-flex justify-content-between align-items-start">
                      <span className="small fw-semibold">{nc.nonconformityName}</span>
                      <Badge color={riskColor(nc.riskDegeri)} pill className="ms-2 flex-shrink-0">
                        {riskLabel(nc.riskDegeri)} ({nc.riskDegeri})
                      </Badge>
                    </div>
                    {nc.oneri && <div className="text-muted mt-1" style={{ fontSize: "0.72rem" }}>{tx("sahaUygunsuzlukList.oneri")}{" "}{nc.oneri}</div>}
                  </button>
                ))}
              </div>
              <button className="btn btn-link btn-sm text-muted p-0 mt-3" onClick={() => setBagStep("risk")}>{tx("sahaUygunsuzlukList.geri")}</button>
            </div>
          )}

          {bagStep === "nc" && (
            <div className="d-flex justify-content-end gap-2 mt-4 border-top pt-3">
              <Button color="light" onClick={() => setBagOpen(false)}>{tx("common:iptal")}</Button>
              <Button color="danger" disabled={!bagSelNc || bagSaving} onClick={handleBagSave}>
                {bagSaving ? <><Spinner size="sm" className="me-1" />{tx("sahaUygunsuzlukList.kaydediliyor")}</> : tx("sahaUygunsuzlukList.uygunsuzlukKaydet")}
              </Button>
            </div>
          )}
        </ModalBody>
      </Modal>
    </PageFrame>
  );
}

export default SahaUygunsuzlukList;
